import React, { FormEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStore } from '../../contexts/StoreContext';
import { usePermissions } from '../../hooks/usePermissions';
import aiService from '../../services/ai.service';
import { AssistantAnswers } from '../../types/ai.types';
import { setAssistantHandoff } from '../../utils/assistantHandoff';
import { useToast } from '../../contexts/ToastContext';
import AiQuotaUpgrade from '../AiQuotaUpgrade/AiQuotaUpgrade';
import Icon from '../Icon/Icon';
import { useAssistantDock } from './assistantDockContext';
import AssistantActionCard from './AssistantActionCard';
import AssistantTrace from './AssistantTrace';
import AssistantAsk from './AssistantAsk';
import './AssistantAsk.scss';
import { applyLiveStep, liveStepIndex, planRunTrace, AssistantTraceStep } from './traceSteps';
import { dockSuggestions, suggestionIcon } from './assistantSuggestions';
import {
  confirmLabel,
  immediateActionPath,
  resultFacts,
  resultTitle,
  toastForResult
} from './assistantLive';
import {
  AssistantChatMessage,
  AssistantThread,
  createId,
  formatThreadClock,
  groupThreads,
  loadThreads,
  saveThreads,
  threadTitle
} from './assistantThreads';
import { visibleAssistantHtml } from './assistantHtml';
import { useDockSwipe } from './useDockSwipe';
import './AssistantDock.scss';

const DOCK_PAGES = [
  '/dashboard',
  '/orders',
  '/centre',
  '/clients',
  '/admin',
  '/notifications',
  '/my-commissions',
  '/profile',
  '/preferences',
  '/help'
];

function isDockPage(pathname: string): boolean {
  return DOCK_PAGES.some((page) => pathname === page || pathname.startsWith(`${page}/`));
}

const AssistantDock: React.FC = () => {
  const { open, setOpen } = useAssistantDock();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { success: toastSuccess } = useToast();
  const { currentStore } = useStore();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const storeId = currentStore?.id;
  const canUse = !permissionsLoading && hasPermission('USE_AI');
  const visible = open && canUse && isDockPage(pathname);

  const [threads, setThreads] = useState<AssistantThread[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quotaBlocked, setQuotaBlocked] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [live, setLive] = useState<{ steps: AssistantTraceStep[]; index: number } | null>(null);
  const [liveDraft, setLiveDraft] = useState('');
  const liveRef = useRef<{ steps: AssistantTraceStep[]; index: number } | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const draftRef = useRef<HTMLTextAreaElement>(null);
  const skipSave = useRef(true);
  const requestSeq = useRef(0);
  const swipe = useDockSwipe(() => setOpen(false), visible);

  useEffect(() => {
    if (!storeId) return;
    skipSave.current = true;
    const saved = loadThreads(storeId);
    setThreads(saved.threads);
    setActiveId(saved.activeId);
    setShowHistory(false);
  }, [storeId]);

  useEffect(() => {
    if (!storeId) return;
    if (skipSave.current) {
      skipSave.current = false;
      return;
    }
    saveThreads(storeId, threads, activeId);
  }, [storeId, threads, activeId]);

  const active = useMemo(
    () => threads.find((thread) => thread.id === activeId) || null,
    [threads, activeId]
  );

  useEffect(() => {
    if (!visible) return;
    const node = listRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [visible, active?.messages.length, busy, showHistory, live, confirmBusy]);

  useEffect(() => {
    const node = draftRef.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${Math.min(node.scrollHeight, 88)}px`;
  }, [draft, visible]);

  liveRef.current = live;
  const liveKey = live ? live.steps.map((step) => step.id).join('|') : '';
  const streamed = Boolean(live && live.steps.some((step) => step.state));

  useEffect(() => {
    if (!liveKey || streamed) return undefined;
    const total = liveKey.split('|').length;
    if (total < 2) return undefined;
    const timer = window.setInterval(() => {
      setLive((prev) => {
        if (!prev || prev.index >= prev.steps.length - 1) return prev;
        return { ...prev, index: prev.index + 1 };
      });
    }, 550);
    return () => window.clearInterval(timer);
  }, [liveKey, streamed]);

  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, setOpen]);

  const patchThread = (threadId: string, recipe: (thread: AssistantThread) => AssistantThread) => {
    setThreads((prev) => prev.map((thread) => (thread.id === threadId ? recipe(thread) : thread)));
  };

  const ensureThread = (): string => {
    if (active && active.messages.length === 0) return active.id;
    const thread: AssistantThread = {
      id: createId(),
      title: 'Nouvelle',
      updatedAt: Date.now(),
      messages: []
    };
    setThreads((prev) => [thread, ...prev].slice(0, 20));
    setActiveId(thread.id);
    return thread.id;
  };

  const startThread = () => {
    if (busy) return;
    setShowHistory(false);
    setError(null);
    if (active && active.messages.length === 0) return;
    ensureThread();
  };

  const sendText = async (raw: string, answers?: AssistantAnswers) => {
    const text = raw.trim();
    if (!storeId || text.length < 2 || busy || quotaBlocked) return;
    const threadId = active && activeId ? activeId : createId();
    const history = (active && active.id === threadId ? active.messages : []).slice(-8).map((item) => ({
      role: item.role,
      content: item.content,
      ...(item.entity && item.entity.id
        ? { ref: { type: item.entity.type, id: item.entity.id, name: item.entity.name } }
        : {})
    }));
    const userMessage: AssistantChatMessage = { id: createId(), role: 'user', content: text };
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setDraft('');
    setBusy(true);
    setError(null);
    setQuotaBlocked(false);
    setLiveDraft('');
    setLive({ steps: [], index: 0 });
    setThreads((prev) => {
      const exists = prev.some((thread) => thread.id === threadId);
      const nextMessageList = (thread: AssistantThread) => ({
        ...thread,
        title: thread.messages.length === 0 ? threadTitle(text) : thread.title,
        updatedAt: Date.now(),
        messages: [...thread.messages, userMessage].slice(-40)
      });
      if (!exists) {
        return [{
          id: threadId,
          title: threadTitle(text),
          updatedAt: Date.now(),
          messages: [userMessage]
        }, ...prev].slice(0, 20);
      }
      return prev.map((thread) => (thread.id === threadId ? nextMessageList(thread) : thread));
    });
    setActiveId(threadId);
    try {
      const reply = await aiService.assistantLive(storeId, {
        message: text,
        history,
        page: `${pathname}${search}`,
        thread_id: threadId,
        ...(answers && Object.keys(answers).length ? { answers } : {})
      }, {
        onStep: (step) => {
          if (requestSeq.current !== seq) return;
          setLive((prev) => {
            const steps = applyLiveStep(prev ? prev.steps : [], step);
            return { steps, index: liveStepIndex(steps) };
          });
        },
        onToken: (chunk) => {
          if (requestSeq.current !== seq) return;
          setLiveDraft((prev) => `${prev}${chunk}`);
        }
      });
      if (requestSeq.current !== seq) return;
      setLive(null);
      setLiveDraft('');
      const assistantMessage: AssistantChatMessage = {
        id: createId(),
        role: 'assistant',
        content: visibleAssistantHtml(reply.message),
        kind: reply.kind,
        action: reply.action,
        questions: reply.questions,
        suggest: reply.suggest || null
      };
      patchThread(threadId, (thread) => ({
        ...thread,
        updatedAt: Date.now(),
        messages: [...thread.messages, assistantMessage].slice(-40)
      }));
    } catch (err: unknown) {
      if (requestSeq.current !== seq) return;
      const axiosErr = err as { response?: { data?: { error?: { code?: string; message?: string } } } };
      const code = axiosErr?.response?.data?.error?.code;
      const message = axiosErr?.response?.data?.error?.message;
      if (code === 'AI_QUOTA_EXCEEDED') {
        setQuotaBlocked(true);
        setError('Quota épuisé');
      } else if (code === 'AI_INACTIVE') {
        setError('Assistant inactif');
      } else {
        setError(message || 'Indisponible');
      }
    } finally {
      if (requestSeq.current === seq) {
        setBusy(false);
        setLive(null);
        setLiveDraft('');
      }
    }
  };

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    void sendText(draft);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void sendText(draft);
    }
  };

  const markMessage = (
    threadId: string,
    messageId: string,
    patch: Partial<AssistantChatMessage>
  ) => {
    patchThread(threadId, (thread) => ({
      ...thread,
      messages: thread.messages.map((item) => (
        item.id === messageId ? { ...item, ...patch } : item
      ))
    }));
  };

  const confirmAction = async (message: AssistantChatMessage) => {
    const action = message.action;
    if (!active || !storeId || !action?.id || confirmBusy || message.confirmed) return;
    setConfirmBusy(true);
    setError(null);
    const threadId = active.id;
    const preview = immediateActionPath(action);
    const runSteps = planRunTrace(action.id);
    setLive({ steps: runSteps, index: 0 });
    markMessage(threadId, message.id, {
      progress: action.type === 'run' && action.id !== 'create_order' ? 'run' : 'open'
    });
    if (action.id === 'create_order') {
      setAssistantHandoff({
        type: 'order',
        prefill: {
          client_name: action.params?.client_name,
          phone: action.params?.phone,
          city: action.params?.city
        }
      });
    }
    if (preview) navigate(preview);
    try {
      const result = await aiService.assistantExecute(storeId, {
        action_id: action.id,
        params: action.params,
        thread_id: threadId
      });
      if (result.handoff?.type === 'search') {
        setAssistantHandoff({
          type: 'search',
          query_tree: result.handoff.query_tree,
          hint: result.handoff.hint,
          apply: result.handoff.apply !== false
        });
      } else if (result.handoff?.type === 'order') {
        setAssistantHandoff({
          type: 'order',
          prefill: result.handoff.prefill || {}
        });
      }
      const path = String(result.path || preview || '');
      if (!path.startsWith('/') || path.startsWith('//')) {
        markMessage(threadId, message.id, { progress: 'error' });
        setError('Redirection impossible');
        return;
      }
      const facts = resultFacts(result);
      const title = resultTitle(result, action.label);
      markMessage(threadId, message.id, {
        confirmed: true,
        progress: 'done',
        result: { title, facts, path },
        entity: result.entity && result.entity.id ? result.entity : null
      });
      toastSuccess(toastForResult(action.id, title), action.label);
      if (path.startsWith('/orders')) {
        const url = new URL(path, window.location.origin);
        url.searchParams.set('assistant', String(Date.now()));
        navigate(`${url.pathname}${url.search}`);
      } else if (path !== preview) {
        navigate(path);
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: { code?: string; message?: string } } } };
      const code = axiosErr?.response?.data?.error?.code;
      const msg = axiosErr?.response?.data?.error?.message;
      markMessage(threadId, message.id, { progress: 'error' });
      if (code === 'AI_QUOTA_EXCEEDED') {
        setQuotaBlocked(true);
        setError('Quota épuisé');
      } else if (code === 'AI_ACTION_INCOMPLETE') {
        setError(msg || 'Information manquante');
      } else {
        setError(msg || 'Action impossible');
      }
    } finally {
      setConfirmBusy(false);
      setLive(null);
    }
  };

  const removeThread = (threadId: string) => {
    setThreads((prev) => prev.filter((thread) => thread.id !== threadId));
    if (activeId === threadId) setActiveId(null);
  };

  if (!visible) return null;

  const messages = active?.messages || [];
  const suggestions = dockSuggestions(pathname);
  const idle = !showHistory && messages.length === 0 && !busy && !live;
  const canSend = draft.trim().length >= 2 && !busy && !quotaBlocked;

  return (
    <>
      <button
        type="button"
        className="assistant-dock__scrim"
        aria-label="Masquer"
        onClick={() => setOpen(false)}
        style={{ ['--dock-shift' as string]: `${swipe.shift}px` }}
      />
      <aside
        ref={swipe.sheetRef}
        className={`assistant-dock${showHistory ? ' is-history' : ''}${idle ? ' is-idle' : ''}${swipe.dragging ? ' is-drag' : ''}`}
        aria-label="Assistant général"
        style={{ ['--dock-shift' as string]: `${swipe.shift}px` }}
        onPointerDown={swipe.onPointerDown}
        onPointerMove={swipe.onPointerMove}
        onPointerUp={swipe.onPointerUp}
        onPointerCancel={swipe.onPointerUp}
      >
      <div className="assistant-dock__grab">
        <div className="assistant-dock__handle" aria-hidden />
      </div>
      <header className="assistant-dock__bar">
        {showHistory ? (
          <button type="button" className="assistant-dock__icon" onClick={() => setShowHistory(false)} aria-label="Retour">
            <Icon name="chevron-left" size={13} />
          </button>
        ) : (
          <span className="assistant-dock__mark" aria-hidden>
            <Icon name="sparkles" size={13} />
          </span>
        )}
        <strong className="assistant-dock__title">{showHistory ? 'Historique' : 'Assistant général'}</strong>
        {!showHistory && (
          <button type="button" className="assistant-dock__icon" onClick={() => setShowHistory(true)} aria-label="Historique" title="Historique">
            <Icon name="history" size={13} />
          </button>
        )}
        <button type="button" className="assistant-dock__icon" onClick={startThread} aria-label="Nouvelle conversation" title="Nouvelle" disabled={busy}>
          <Icon name="plus" size={13} />
        </button>
        <button type="button" className="assistant-dock__icon" onClick={() => setOpen(false)} aria-label="Masquer" title="Masquer">
          <Icon name="x" size={13} />
        </button>
      </header>

      {showHistory ? (
        <div className="assistant-dock__history">
          {threads.length === 0 && <p className="assistant-dock__empty">Aucune conversation</p>}
          {groupThreads(threads).map((group) => (
            <section key={group.id} className="assistant-dock__day" aria-label={group.label}>
              <p className="assistant-dock__day-label">{group.label}</p>
              {group.threads.map((thread) => (
                <div key={thread.id} className={`assistant-dock__past${thread.id === activeId ? ' is-on' : ''}`}>
                  <button
                    type="button"
                    className="assistant-dock__past-open"
                    title={thread.title}
                    onClick={() => {
                      setActiveId(thread.id);
                      setShowHistory(false);
                    }}
                  >
                    <span>{threadTitle(thread.title)}</span>
                    <em>{formatThreadClock(thread.updatedAt)}</em>
                  </button>
                  <button type="button" className="assistant-dock__icon assistant-dock__icon--quiet" onClick={() => removeThread(thread.id)} aria-label="Supprimer">
                    <Icon name="trash-2" size={12} />
                  </button>
                </div>
              ))}
            </section>
          ))}
        </div>
      ) : (
        <>
          <div className="assistant-dock__thread" ref={listRef} aria-busy={busy || confirmBusy}>
            {messages.length === 0 && !busy && (
              <div className="assistant-dock__welcome">
                <p className="assistant-dock__lead">Commandes, filtres, écrans.</p>
                {suggestions.map((suggestion) => (
                  <button key={suggestion} type="button" className="assistant-dock__sug" onClick={() => void sendText(suggestion)}>
                    <span className="assistant-dock__sug-icon" aria-hidden>
                      <Icon name={suggestionIcon(suggestion)} size={12} />
                    </span>
                    <span className="assistant-dock__sug-label">{suggestion}</span>
                    <span className="assistant-dock__sug-go" aria-hidden>
                      <Icon name="chevron-right" size={10} />
                    </span>
                  </button>
                ))}
              </div>
            )}
            {messages.map((message, index) => (
              <div key={message.id} className={`assistant-dock__msg assistant-dock__msg--${message.role}${message.kind === 'refuse' ? ' is-refuse' : ''}${message.kind === 'ask' ? ' is-ask' : ''}`}>
                {message.role === 'assistant' ? (
                  <div
                    className="assistant-dock__html"
                    dangerouslySetInnerHTML={{ __html: visibleAssistantHtml(message.content) }}
                  />
                ) : (
                  <p>{message.content}</p>
                )}
                {message.role === 'assistant' && message.suggest && messages[messages.length - 1]?.id === message.id && (
                  <button
                    type="button"
                    className="assistant-dock__suggest"
                    onClick={() => void sendText(message.suggest?.prompt || '')}
                  >
                    {message.suggest.label}
                  </button>
                )}
                {message.role === 'assistant' && message.kind === 'ask' && message.questions && message.questions.length > 0 && messages[messages.length - 1]?.id === message.id && (
                  <AssistantAsk
                    questions={message.questions}
                    onDone={(text, answers) => void sendText(text, answers)}
                  />
                )}
                {message.role === 'assistant' && message.kind === 'confirm' && message.action && (
                  message.confirmed || message.progress ? (
                    <AssistantActionCard
                      step={message.progress || (message.confirmed ? 'done' : 'run')}
                      label={message.result?.title || message.action.label}
                      facts={message.result?.facts}
                      path={message.result?.path || message.action.path || undefined}
                      onOpen={(next) => navigate(next)}
                    />
                  ) : messages.slice(index + 1).some((item) => item.kind === 'confirm' && item.action) ? null : (
                    <button
                      type="button"
                      className="assistant-dock__confirm"
                      disabled={confirmBusy}
                      onClick={() => void confirmAction(message)}
                    >
                      {confirmLabel()}
                    </button>
                  )
                )}
              </div>
            ))}
            {live && live.steps.length > 0 && (
              <div className="assistant-dock__live">
                <AssistantTrace steps={live.steps} activeIndex={live.index} />
                {liveDraft ? (
                  <div
                    className="assistant-dock__html"
                    dangerouslySetInnerHTML={{ __html: visibleAssistantHtml(liveDraft) }}
                  />
                ) : null}
              </div>
            )}
          </div>
          {error && <p className="assistant-dock__error" role="alert">{error}</p>}
          {quotaBlocked && storeId && (
            <AiQuotaUpgrade
              storeId={storeId}
              onUpgraded={() => {
                setQuotaBlocked(false);
                setError(null);
              }}
            />
          )}
          <form className="assistant-dock__composer" onSubmit={submit}>
            <textarea
              ref={draftRef}
              rows={1}
              value={draft}
              placeholder="Une question ou une action…"
              aria-label="Message à l’assistant"
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={onKeyDown}
              disabled={busy || quotaBlocked}
              maxLength={600}
            />
            <button type="submit" className={canSend || busy ? 'is-ready' : ''} disabled={!canSend} aria-label="Envoyer">
              <Icon name={busy ? 'loader' : 'send'} size={13} />
            </button>
          </form>
        </>
      )}
    </aside>
    </>
  );
};

export default AssistantDock;
